import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { type DocumentNode, type TypedDocumentNode, type WatchQueryFetchPolicy } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type GroupQueryState<TData> = {
  data: TData[];
  total: number;
  loading: boolean;
  /** Offset of the first row in `data` within the total dataset. */
  dataOffset: number;
  /** Jump to a specific offset. The provider re-fetches from that position. */
  fetchPage: (offset: number, limit: number) => void;
  /** Stable token that flips only on filter/sort/search changes. */
  variablesKey: string;
};

type Connection = Omittable<{
  edges: Array<{ node: unknown }>;
  pageInfo: { hasNextPage: boolean };
  total?: number;
}>;

export type UseGroupQueryOptions<TData> = Omittable<{
  /** Unique key for this group. */
  groupKey: string;
  /** GraphQL document to execute (must follow Relay connection shape). */
  document: DocumentNode | TypedDocumentNode;
  /** Query variables (input, first, etc.). */
  variables: Record<string, unknown>;
  /**
   * Extract the Relay connection object from the query result.
   * Example: `(data) => data?.dashboardActivityDrilldown`
   */
  getConnection: (data: any) => Connection | undefined | null;
  /**
   * Report this group's current state to the parent.
   */
  onStateChange: (key: string, state: GroupQueryState<TData>) => void;
  /**
   * Fires when query data arrives. Use to extract sibling connections
   * (e.g. stages, roles, teams) from the same query result.
   */
  onData?: (rawQueryData: any) => void;
  /**
   * Apollo fetch policy. Defaults to "cache-and-network".
   * Use "network-only" for data that must always be fresh (e.g. trash).
   */
  fetchPolicy?: WatchQueryFetchPolicy;
  /**
   * When false, the group does not subscribe to its query (no watchQuery) and
   * reports an empty, not-loading state. Defaults to true.
   */
  enabled?: boolean;
}>;

/** Stable empty array so a disabled group reports the same reference every render. */
const EMPTY_ROWS: readonly never[] = [];

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Runs a paginated query for a single group and reports its state upward
 * via `onStateChange`.
 *
 * Pagination is offset-based: the DataTable calls `fetchPage(offset, limit)`
 * when the virtualizer scrolls to an unloaded region. The hook updates
 * its internal offset override, which changes the query variables and
 * triggers Apollo to re-fetch from that position.
 */
export function useGroupQuery<TData>({
  groupKey,
  document,
  variables,
  getConnection,
  onStateChange,
  onData,
  fetchPolicy = "cache-and-network",
  enabled = true,
}: UseGroupQueryOptions<TData>) {
  const [skipOverride, setSkipOverride] = useState<number>(0);

  // Reset to page 0 when the query context changes (filter/sort/search).
  const variablesKey = JSON.stringify(variables);
  const prevVariablesKeyRef = useRef(variablesKey);
  // The context minus its ordering: two contexts with one slice key select
  // the same rows and differ only in the order they come back. Every list
  // names its sort `orderBy`, so the key is the variables with that omitted
  // (`JSON.stringify` drops an undefined member).
  const sliceKey = JSON.stringify({ ...variables, orderBy: undefined });
  const prevSliceKeyRef = useRef(sliceKey);

  // Always include skip in variables for consistent cache keys.
  const effectiveVariables = useMemo(() => {
    return { ...variables, skip: skipOverride };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- variablesKey stands for the variables object contents
  }, [variablesKey, skipOverride]);

  const client = useApolloClient();
  // The cache's complete answer for this context's first page, or null where
  // it holds none. A READ, never a fetch: whatever an earlier visit to the same
  // context put in the store is already there, and reading it during render is
  // what lets a switch back to a slice seen before paint its rows in the same
  // frame as the switch. The subscription below still runs — cache-and-network
  // revalidates — and re-emits the same answer after paint. Without this read
  // the answer arrives one frame late, and that frame paints placeholders over
  // rows the store had all along.
  const readCachedFirstPage = () =>
    enabled
      ? client.readQuery({ query: document, variables: { ...variables, skip: 0 } })
      : null;
  // Tag queryData with the variables AND the skip it was fetched for. A result
  // answers exactly one query context, so reporting it under another is wrong
  // twice over: the previous page's rows under the new dataOffset, and the
  // previous filter's rows and total under the new `where`. Both windows are
  // real — one render between the state change and the watchQuery effect for a
  // cache hit, the whole round trip for a miss. So during render we treat
  // queryState as stale (null) unless both tags match what is being asked now.
  const [queryState, setQueryState] = useState<{
    data: any;
    skip: number;
    variablesKey: string;
  } | null>(() => {
    const cached = readCachedFirstPage();
    return cached ? { data: cached, skip: 0, variablesKey } : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const fetchedForSkip = (effectiveVariables as { skip?: number }).skip ?? 0;
    const fetchedForKey = variablesKey;
    const observable = client.watchQuery({
      query: document,
      variables: effectiveVariables,
      fetchPolicy,
    });

    const subscription = observable.subscribe({
      next: (result) => {
        if (result.data !== undefined) {
          setQueryState({
            data: result.data,
            skip: fetchedForSkip,
            variablesKey: fetchedForKey,
          });
        }
        setLoading(result.loading);
      },
      error: () => {
        // A failed context has no rows to stand behind the held ones.
        heldRef.current = { data: EMPTY_ROWS as unknown as TData[], total: 0 };
        setLoading(false);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [client, document, effectiveVariables, fetchPolicy, enabled, variablesKey]);

  const queryData =
    queryState && queryState.skip === skipOverride && queryState.variablesKey === variablesKey
      ? queryState.data
      : null;
  const connection = getConnection(queryData);
  const initialLoading = loading && !queryData && skipOverride === 0;

  // The row IS the connection node — the generated type travels from the query
  // to the leaf component unchanged. Keying on `edges` alone keeps the array
  // reference stable across renders; a fresh array every render would fire the
  // onStateChange effect endlessly and exceed the update depth.
  const mappedData = useMemo(() => {
    if (!connection?.edges) return [];
    return connection.edges.map((edge) => edge.node as TData);
  }, [connection?.edges]);

  // Preserve total across page transitions so the virtualizer keeps its
  // height and scroll position while a new page loads.
  const totalRef = useRef(0);

  // When query context changes (filter/sort/search), reset to page 0 and clear
  // the stale total so the virtualizer doesn't over-size. `loading` flips here
  // rather than in the effect that re-subscribes: the render that first sees a
  // new key has no result for it yet, and a state of "settled, zero rows" is
  // how a consumer is told the new context is EMPTY.
  if (prevVariablesKeyRef.current !== variablesKey) {
    prevVariablesKeyRef.current = variablesKey;
    if (skipOverride !== 0) setSkipOverride(0);
    totalRef.current = 0;
    setLoading(true);
    const cached = readCachedFirstPage();
    if (cached) setQueryState({ data: cached, skip: 0, variablesKey });
  }

  if (connection?.total != null) totalRef.current = connection.total;
  const total = totalRef.current;

  // The rows last answered for THIS slice, held across a RE-SORT whose first
  // page the cache cannot answer. A sort never fetched before has nothing for
  // one round trip, and reporting that gap swaps the rows for skeleton rows
  // and back — the swap itself is the flash. The previous rows stay instead,
  // settled, until the new order replaces them: they are the same set, in
  // the order the user just left. The hold never crosses a slice — a filter
  // or search change selects different rows, and the previous slice's rows
  // under the new predicate are wrong, so that path still reports the gap
  // (the test beside this hook pins it). First page only: a page jump inside
  // a context keeps the table's own windowed cache. A sort that comes back
  // empty still lands as settled-zero the moment its answer arrives, and an
  // error drops the hold (see the subscription's error branch).
  const heldRef = useRef<{ data: TData[]; total: number }>({
    data: EMPTY_ROWS as unknown as TData[],
    total: 0,
  });
  if (prevSliceKeyRef.current !== sliceKey) {
    prevSliceKeyRef.current = sliceKey;
    heldRef.current = { data: EMPTY_ROWS as unknown as TData[], total: 0 };
  }
  if (queryData != null) heldRef.current = { data: mappedData, total };
  const holding =
    enabled &&
    queryData == null &&
    skipOverride === 0 &&
    heldRef.current.data.length > 0;
  const reportedData = holding ? heldRef.current.data : mappedData;
  const reportedTotal = holding ? heldRef.current.total : total;
  const reportedLoading = holding ? false : initialLoading;

  const dataOffset = skipOverride;

  // Stable fetchPage — identity never changes.
  const stableFetchPage = useCallback((offset: number, _limit: number) => {
    setSkipOverride(offset);
  }, []);

  // Stable refs to avoid effect re-fires if parent passes new callback identities.
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;
  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  // Report sibling data when query result arrives.
  useEffect(() => {
    if (queryData != null) onDataRef.current?.(queryData);
  }, [queryData]);

  // A disabled group reports an explicit empty, not-loading state — otherwise the
  // last-loaded rows linger (mappedData is unchanged, so without `enabled` in the
  // deps this effect wouldn't re-fire on the toggle) and the parent keeps rendering
  // a stale column.
  //
  // A LAYOUT effect, so the consuming table re-renders with the new state
  // before the browser paints. The render that changes a slice has no report
  // for it yet — the parent still holds the previous slice's rows under the new
  // predicate — and a passive effect lands after paint, which puts that stale
  // frame on screen on every switch.
  useLayoutEffect(() => {
    onStateChangeRef.current(groupKey, {
      data: enabled ? reportedData : (EMPTY_ROWS as unknown as TData[]),
      total: enabled ? reportedTotal : 0,
      loading: enabled ? reportedLoading : false,
      dataOffset,
      fetchPage: stableFetchPage,
      variablesKey,
    });
  }, [
    enabled,
    groupKey,
    reportedData,
    reportedTotal,
    reportedLoading,
    dataOffset,
    stableFetchPage,
    variablesKey,
  ]);
}
